// File: typescript/apps/front-end/src/lib/promisePolyfills.ts
//
// Minimal runtime polyfill for Promise.allSettled (ES2020). esbuild's `target: es2017` only
// down-levels *syntax* — it injects no runtime shims for missing built-in methods — so an old or
// embedded webview lacking Promise.allSettled hits the raw call at ReactMounter.mountAll() and throws
// synchronously, rejecting the awaited cold-boot mount and stalling the whole bootstrap (the loading
// splash never dismisses). Prod-log triage (2026-07-05) saw exactly this: "Promise.allSettled is not
// a function" on embedded-webview UAs.
//
// Installed as the first step of runOneTimeGlobalSetup() (before any island mounts), mirroring the
// domReconciliationGuard "install an early runtime guard" precedent. Idempotent and a no-op on modern
// engines, so it costs nothing where the API is native.

export function installPromisePolyfills(): void {
    if (typeof Promise === "undefined") {
        return;
    }

    const promiseCtor = Promise as unknown as {
        allSettled?: (values: Iterable<unknown>) => Promise<unknown[]>;
        resolve: (value: unknown) => Promise<unknown>;
        all: (values: Iterable<unknown>) => Promise<unknown[]>;
    };

    if (typeof promiseCtor.allSettled === "function") {
        return;
    }

    promiseCtor.allSettled = function allSettled(values: Iterable<unknown>): Promise<unknown[]> {
        const wrapped = Array.from(values, (value) =>
            promiseCtor.resolve(value).then(
                (v) => ({ status: "fulfilled" as const, value: v }),
                (reason) => ({ status: "rejected" as const, reason }),
            ),
        );
        return promiseCtor.all(wrapped);
    };
}
